import { debounce } from '@mui/material';
import { memo, useEffect } from 'react';
import { Control, FieldValues, useWatch } from 'react-hook-form';

type FormChangeDebouncerProps<T extends FieldValues> = {
  debounceDelay?: number;
  control: Control<T>;
  onChange: (data: T) => void;
};

const InnerFormChangeDebouncer = <T extends FieldValues>({
  debounceDelay = 125,
  control,
  onChange,
}: FormChangeDebouncerProps<T>) => {
  const data = useWatch({ control });

  useEffect(() => {
    const debouncedOnChange = debounce(() => {
      onChange(data as T);
    }, debounceDelay);
    debouncedOnChange();

    return () => debouncedOnChange?.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return null;
};

// using `() => true` to always memorize the component and
// prevent external changes from causing a re-render
export const FormChangeDebouncer = memo(InnerFormChangeDebouncer, () => true) as typeof InnerFormChangeDebouncer;
